var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, } from "react";
import { pluralizedCount } from "utils-library/dist/utils";
import { arrayRemove, arrayInsert, } from "utils-library/dist/array";
import { ButtonBar } from "../ButtonBar";
import { Button, EButtonSize, EButtonColor, } from "../Button";
import { LabelContainer, ELabelContainerVariant, } from "../LabelContainer";
import { Box } from "../Box";
import { InputSwitch, EInputSwitchPosition, } from "../InputSwitch";
import { Condition } from "../Condition";
import { HelperText } from "../HelperText";
import { ModalDialog } from "../ModalDialog";
import { IconStatus, EIconStatus, } from "../IconStatus";
import { useConfirm } from "../useConfirm";
import { useLocalStorageState } from "../useLocalStorageState";
import { useClipboardPaste } from "../useClipboardPaste";
import { EGeoCoordinatesEditorSize } from "./interfaces";
import { InputCoordinate } from "./components/InputCoordinate";
import { getGeoPositionsByStrings } from "./utils/getGeoPositionsByStrings";
import { createIcon } from "../IconComponent";
import CoordinatesEditorIcon from "@mui/icons-material/EditLocation";
import MuiIconElevation from '@mui/icons-material/FilterHdr';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import PasteCoordinatesIcon from '@mui/icons-material/ContentPasteGo';
import OkIcon from '@mui/icons-material/Done';
const ElevationIcon = createIcon.byMuiIcon(MuiIconElevation);
/**
 * Input Coordinates. Needs height from the parent.
 */
export const InputCoordinates = ({ label = "Coordinates editor", variant = ELabelContainerVariant.OUTLINED, size = EGeoCoordinatesEditorSize.MEDIUM, readOnly = false, coordinates, maxCoordinates, footer = null, onElevationResolve, onFocus, onChange, }) => {
    const { confirm, confirmViewer, } = useConfirm();
    const [showAlt, setShowAlt] = useLocalStorageState({
        key: 'CoordinatesEditor-component-showAlt',
        default: false,
    });
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const { hasClipboard, clipboardText, } = useClipboardPaste();
    const [showPasteCoordinatesReport, setShowPasteCoordinatesReport,] = useState(null);
    const handleFocus = (index) => {
        if (focusedIndex === index)
            return;
        setFocusedIndex(index);
        onFocus && onFocus(index);
    };
    const handleAddFirstCoordinate = () => {
        onChange([{
                lat: 0,
                lng: 0,
            }]);
    };
    const handleCleanAllCoordinates = () => __awaiter(void 0, void 0, void 0, function* () {
        const deleteConfirmation = yield confirm({
            title: 'Delete confirmation',
            message: `Are you sure you want to delete ${pluralizedCount({
                this: true,
                unit: 'coordinate',
                items: coordinates,
            })}?`,
        });
        if (deleteConfirmation)
            onChange([]);
    });
    const handlePasteCoordinatesClick = () => {
        const parseCoordinates = getGeoPositionsByStrings(clipboardText.split('\n'));
        if (!!maxCoordinates && coordinates.length + parseCoordinates.coordinates.length > maxCoordinates) {
            setShowPasteCoordinatesReport({
                iconStatus: EIconStatus.ERROR,
                title: 'Paste coordinates',
                message: `The coordinates detected in the clipboard (${parseCoordinates.coordinates.length}) along with the existing ones (${coordinates.length}) exceed the maximum coordinates limitation of ${maxCoordinates}. Please remove some and try again.`,
                errors: null,
            });
            return;
        }
        onChange([
            ...coordinates,
            ...parseCoordinates.coordinates,
        ]);
        setShowPasteCoordinatesReport({
            iconStatus: (() => {
                if (parseCoordinates.coordinates.length) {
                    return parseCoordinates.errors.length
                        ? EIconStatus.WARNING
                        : EIconStatus.SUCCESS;
                }
                else {
                    return EIconStatus.ERROR;
                }
            })(),
            title: 'Paste coordinates',
            message: parseCoordinates.errors.length
                ? (_jsxs(_Fragment, { children: [_jsx(Condition, { if: !!parseCoordinates.coordinates.length, then: _jsxs(_Fragment, { children: [pluralizedCount({
                                        unit: 'coordinate',
                                        items: parseCoordinates.coordinates,
                                    }), " ", "added"] }), else: "No coordinated added" }), _jsxs(Condition, { if: !!parseCoordinates.errors.length, children: [", and there", " ", pluralizedCount({
                                    isAtFront: true,
                                    unit: 'error',
                                    items: parseCoordinates.errors,
                                }), "!"] })] }))
                : `Success, ${pluralizedCount({
                    unit: 'coordinate',
                    items: parseCoordinates.coordinates,
                })} added`,
            errors: (_jsxs(Box, { show: !!parseCoordinates.errors.length, children: [_jsx("b", { children: "Errors:" }), _jsx("ul", { children: parseCoordinates.errors.map((error, index) => (_jsx("li", { children: error }, index))) })] })),
        });
    };
    const handleModalOkClick = () => setShowPasteCoordinatesReport(null);
    const handleDelete = (index) => __awaiter(void 0, void 0, void 0, function* () {
        const deleteConfirmation = yield confirm({
            title: 'Delete confirmation',
            message: 'Are you sure you want to delete this coordinate?',
            children: (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("br", {}), _jsxs("b", { children: ["Coordinate no: ", index + 1] }), _jsx("br", {}), _jsx("br", {}), _jsxs("span", { children: [_jsx("b", { children: "LAT:" }), " ", coordinates[index].lat] }), _jsx("br", {}), _jsxs("span", { children: [_jsx("b", { children: "LNG:" }), " ", coordinates[index].lng] }), _jsx("br", {}), _jsxs("span", { children: [_jsx("b", { children: "ELEV:" }), " ", coordinates[index].alt] }), _jsx("br", {})] })),
            labelConfirmIcon: createIcon.byMuiIcon(DeleteIcon),
            labelConfirmButton: 'Delete it',
        });
        if (deleteConfirmation)
            onChange(arrayRemove(coordinates, index));
    });
    const handleInsert = (index) => {
        onChange(arrayInsert(coordinates, index, Object.assign({}, coordinates[index])));
    };
    const handleChange = (index, position) => {
        const newPositions = [...coordinates];
        newPositions[index] = position;
        onChange(newPositions);
    };
    const disableAdd = !!maxCoordinates && coordinates.length === maxCoordinates;
    return (_jsxs(LabelContainer, { sx: { background: theme => theme.palette.background.default }, dataComponentName: "InputCoordinates", Icon: createIcon.byMuiIcon(CoordinatesEditorIcon), label: label, variant: variant, fullHeight: true, overFlowY: true, labelRightContent: _jsx(Box, { show: !!coordinates.length, children: _jsx(InputSwitch, { ariaLabel: "Show elevation field", switchPosition: EInputSwitchPosition.RIGHT, Icon: ElevationIcon, label: "Elevation", value: showAlt, onChange: setShowAlt }) }), helperContent: _jsxs(_Fragment, { children: [_jsx(HelperText, { sx: { py: theme => theme.spacing(0.5) }, Icon: createIcon.byMuiIcon(InfoIcon), children: "The LAT/LNG decimal places can be up to 14, with all others being disregarded." }), footer] }), children: [_jsxs(Box, { sx: { overflowY: 'auto' }, fullHeight: true, dataComponentName: "InputCoordinates-scrollableContent", children: [coordinates.map((position, index) => (_jsx(InputCoordinate, { size: size, readOnly: readOnly, disableAdd: disableAdd, showAlt: showAlt, focused: focusedIndex === index, coordinate: position, onElevationResolve: onElevationResolve, onFocus: () => handleFocus(index), onInsert: () => handleInsert(index), onDelete: () => handleDelete(index), onChange: position => handleChange(index, position) }, index))), _jsxs(ButtonBar, { show: !readOnly, noHorizontalSpace: true, children: [_jsx(Button, { Icon: createIcon.byMuiIcon(AddCircleIcon), show: !disableAdd && !coordinates.length, size: EButtonSize.SMALL, onClick: handleAddFirstCoordinate, children: "Add the 1st coordinate" }), _jsx(Button, { Icon: createIcon.byMuiIcon(PasteCoordinatesIcon), disabled: !hasClipboard || disableAdd, size: EButtonSize.SMALL, onClick: handlePasteCoordinatesClick, children: "Paste coordinates" }), _jsx(Button, { Icon: createIcon.byMuiIcon(DeleteIcon), show: !!coordinates.length, size: EButtonSize.SMALL, color: EButtonColor.ERROR, onClick: handleCleanAllCoordinates, children: "Delete all" })] })] }), _jsx(Condition, { if: !!showPasteCoordinatesReport, children: _jsxs(ModalDialog, { show: true, title: (showPasteCoordinatesReport === null || showPasteCoordinatesReport === void 0 ? void 0 : showPasteCoordinatesReport.title) || "", buttons: [
                        {
                            Icon: createIcon.byMuiIcon(OkIcon),
                            label: 'Ok',
                            onClick: handleModalOkClick,
                        },
                    ], children: [_jsx(IconStatus, { status: (showPasteCoordinatesReport === null || showPasteCoordinatesReport === void 0 ? void 0 : showPasteCoordinatesReport.iconStatus) || EIconStatus.HELP, sizePx: 64, delayAnimationStartMs: 150, message: (showPasteCoordinatesReport === null || showPasteCoordinatesReport === void 0 ? void 0 : showPasteCoordinatesReport.message) || "Internal error 20230920100524" }), showPasteCoordinatesReport === null || showPasteCoordinatesReport === void 0 ? void 0 : showPasteCoordinatesReport.errors] }) }), confirmViewer] }));
};
